import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import styled from 'styled-components';
import toast from '~~elements/Toast';
import Loading from '~~elements/Loading';
import Multilingual from '~~utils/Multilingual';
import { GameResource, FormalGameResource } from '~~apis/resource';
import * as R from 'ramda';
import { StyledDiv } from '../Settings/Styles';
import { useTournamentDispatch, useTournamentState } from './useTournament';
import { StyledBackIcon } from './styled';
import FormalGameOverview from './FormalGame/FormalGameOverview';
import FormalGameBoxBatting from './FormalGame/FormalGameBoxBatting';
import FormalGameBoxPitching from './FormalGame/FormalGameBoxPitching';
import { verify } from './FormalGame/validator';
import FormalGamePAList from './FormalGame/FormalGamePAList';
import FormalGameEvents from './FormalGame/FormalGameEvents';
import FormalGameRunners from './FormalGame/FormalGameRunners';
import FormalGameChances from './FormalGame/FormalGameChances';
import FormalGameSubstitutions from './FormalGame/FormalGameSubstitutions';
import { useFormalGameDispatch, useFormalGameState } from './FormalGame/useFormalGame';
import { toRunnerList } from './FormalGame/FormalGameRunners';
import { toTableRows } from './FormalGame/records';
import FormalGameRecord from './FormalGame/FormalGameRecord';

const MessageDiv = styled.div`
  display: block;
  width: 100%;
  > p {
    margin: 2.5px 0;
    font-size: 0.9rem;
    &.missing {
      color: brown;
    }
    &.error {
      color: red;
    }
  }
`;

const CustomDiv = styled(StyledDiv)`
  .update-pending-list {
    margin: 5px 0;
    font-size: 12px;
    p,
    ul,
    li {
      margin: 3px 0;
      .cancel-update {
        width: 30px;
        padding: 0;
        margin: 0 5px 0 0;
      }
    }
  }
`;

const getInGamePlayers = (box) => {
  const players = {};
  ['battings', 'pitchings', 'fieldings', 'records'].forEach((tab) => {
    box[tab].forEach((row) => {
      if (!row.player || players[row.player.uniqid]) {
        return;
      }

      players[row.player.uniqid] = R.clone(row.player);
    });
  });
  return R.sort(R.ascend(R.prop('number')), Object.values(players));
};

const partMapping = {
  overview: '球員名單',
  awayRecord: 'AWAY-單場紀錄',
  awayBoxBatting: 'AWAY-BOX-打擊',
  awayBoxPitching: 'AWAY-BOX-投球',
  awayPAList: 'AWAY-打席資訊',
  awayEvents: 'AWAY-打席事件',
  awayRunners: 'AWAY-跑壘事件',
  awayChances: 'AWAY-防守事件',
  awaySubstitutions: 'AWAY-換人事件',
  homeRecord: 'HOME-單場紀錄',
  homeBoxBatting: 'HOME-BOX-打擊',
  homeBoxPitching: 'HOME-BOX-投球',
  homePAList: 'HOME-打席資訊',
  homeEvents: 'HOME-打席事件',
  homeRunners: 'HOME-跑壘事件',
  homeChances: 'HOME-防守事件',
  homeSubstitutions: 'HOME-換人事件',
};

const FormalGame = ({ isPlatformAdmin, organizer, game }) => {
  const { tournamentInfo } = useTournamentState();
  const tournamentDispatch = useTournamentDispatch();
  const { statsUpdateRows } = useFormalGameState();
  const formalGameDispatch = useFormalGameDispatch();

  const [gameTitle, setGameTitle] = useState('');

  const [part, setPart] = useState('overview');

  const [inPlayLoading, setInPlayLoading] = useState(true);
  const [gameWithInPlay, setGameWithInPlay] = useState(null);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [homePlayers, setHomePlayers] = useState([]);
  const [awayRecordTable, setAwayRecordTable] = useState([]);
  const [homeRecordTable, setHomeRecordTable] = useState([]);

  const [updating, setUpdating] = useState(false);

  const [messages, setMessages] = useState({
    missingMessages: [],
    errorMessages: [],
  });

  useEffect(() => {
    const seq = `G${game.seq}`;
    const away = !game.away.team ? Multilingual(`GAME.TEAM_NONE`) : game.away.team;
    const home = !game.home.team ? Multilingual(`GAME.TEAM_NONE`) : game.home.team;
    const date = moment(game.info.scheduled_start_at).format('YYYY/MM/DD HH:mm');
    setGameTitle(`${seq} ${away} vs ${home} ${date}`);

    setInPlayLoading(true);
    GameResource.getGame({ seasonUniqid: tournamentInfo.uniqid, gameUniqid: game.uniqid })
      .then(({ data }) => {
        setGameWithInPlay({
          ...data,
        });
        const awayPlayers = getInGamePlayers(data.away.box);
        const homePlayers = getInGamePlayers(data.home.box);
        setAwayPlayers(awayPlayers);
        setHomePlayers(homePlayers);
        setAwayRecordTable(toTableRows(data.away.box.pitchings, data.away.box.battings, data.away.box.records));
        setHomeRecordTable(toTableRows(data.home.box.pitchings, data.home.box.battings, data.home.box.records));
        setInPlayLoading(false);
      })
      .catch((e) => {
        console.error(e);
        toast(e, { status: 'error' });
      });
  }, [game, tournamentInfo.uniqid]);

  useEffect(() => {
    if (!gameWithInPlay) {
      return;
    }

    setMessages(verify(gameWithInPlay.away.box, gameWithInPlay.home.box, gameWithInPlay.PA_list));
  }, [gameWithInPlay]);

  const handleStatsUpdate = (statsType, cloneRow) => {
    setUpdating(true);
    const side = part.slice(0, 4);
    let toUpdateData = cloneRow;
    if (statsType === 'record') {
      const filteredRecords = R.filter(
        (r) => r.player.uniqid !== cloneRow.player.uniqid,
        gameWithInPlay[side.toLowerCase()].box.records,
      );
      Object.entries(cloneRow).forEach(([key, value]) => {
        if (typeof value !== 'boolean' || !value) {
          return;
        }

        filteredRecords.push({
          player: cloneRow.player,
          player_type: cloneRow.player.player_type,
          value: key,
          comment: '',
        });
      });
      toUpdateData = filteredRecords;
    }

    FormalGameResource.updateStats({
      seasonUniqid: tournamentInfo.uniqid,
      gameUniqid: game.uniqid,
      data: toUpdateData,
      statsType,
      side,
    })
      .then(({ data }) => {
        setGameWithInPlay({
          ...data,
        });
        tournamentDispatch({
          type: 'UPDATE_GAME',
          payload: {
            ...data,
          },
        });
        toast('更新成功', { status: 'success', second: 1 });
        formalGameDispatch({ type: 'RESET_STATS_UPDATE_ROWS' });
      })
      .catch((e) => {
        toast(e, { status: 'error', second: 5 });
      })
      .finally(() => {
        setUpdating(false);
      });
  };

  const handleRecordUpdate = (rowIndex, cloneRow) => {
    console.log(rowIndex, cloneRow);
    handleStatsUpdate('record', cloneRow);
  };

  const handleBattingUpdate = (rowIndex, cloneRow) => {
    console.log(rowIndex, cloneRow);
    handleStatsUpdate('batting', cloneRow);
  };

  const handlePitchingUpdate = (rowIndex, cloneRow) => {
    console.log(rowIndex, cloneRow);
    handleStatsUpdate('pitching', cloneRow);
  };

  const handlePAListUpdate = (rowIndex, cloneRow) => {
    console.log(rowIndex, cloneRow);
    if (cloneRow) {
      handleStatsUpdate('pa', [cloneRow]);
      return;
    }
    batchUpdate('pa');
  };

  const handleEventUpdate = (rowIndex, cloneRow) => {
    console.log(rowIndex, cloneRow);
    // 目前沒有可更新欄位
  };

  const handleRunnerUpdate = (rowIndex, cloneRow) => {
    console.log(rowIndex, cloneRow);
    if (cloneRow) {
      handleStatsUpdate('runner', [cloneRow]);
      return;
    }
    batchUpdate('runner');
  };

  const handleRowSave = (rowIndex, toUpdateColumn, newValue) => {
    console.log(rowIndex, toUpdateColumn, newValue);
    formalGameDispatch({ type: 'SAVE_STATS_UPDATE_ROW', payload: { rowIndex, toUpdateColumn, newValue } });
  };

  const partLink = (targetPart) => {
    return (
      <label
        ley={targetPart}
        className={`section-list--item ${targetPart === part ? 'selected' : ''} ${updating ? 'disabled' : ''}`}
        onClick={() => {
          formalGameDispatch({ type: 'RESET_STATS_UPDATE_ROWS' });
          setPart(targetPart);
        }}
      >
        {partMapping[targetPart]}
      </label>
    );
  };

  const batchUpdate = (statsType) => {
    const batchUpdateRows = getBatchUpdateRows();
    const cloneRows = Object.entries(statsUpdateRows).map(([rowIndex, toUpdates]) => {
      const targetRow = batchUpdateRows[rowIndex];
      toUpdates.forEach((toUpdate) => {
        targetRow[toUpdate.toUpdateColumn] = toUpdate.newValue;
      });
      return targetRow;
    });
    handleStatsUpdate(statsType, cloneRows);
  };

  const getBatchUpdateRows = () => {
    switch (part) {
      case 'awayPAList':
        return R.filter((PA) => PA.side === 'AWAY', gameWithInPlay.PA_list);
      case 'awayRunners':
        return toRunnerList(R.filter((PA) => PA.side === 'AWAY', gameWithInPlay.PA_list));
      case 'homePAList':
        return R.filter((PA) => PA.side === 'HOME', gameWithInPlay.PA_list);
      case 'homeRunners':
        return toRunnerList(R.filter((PA) => PA.side === 'HOME', gameWithInPlay.PA_list));
      default:
        return [];
    }
  };

  const getSection = () => {
    switch (part) {
      case 'overview':
        return (
          <FormalGameOverview tournamentInfo={tournamentInfo} isPlatformAdmin={isPlatformAdmin} game={gameWithInPlay} />
        );
      case 'awayRecord':
        return <FormalGameRecord players={awayRecordTable} handleUpdate={handleRecordUpdate} />;
      case 'awayBoxBatting':
        return (
          <FormalGameBoxBatting
            players={awayPlayers}
            battings={gameWithInPlay.away.box.battings}
            handleUpdate={handleBattingUpdate}
          />
        );
      case 'awayBoxPitching':
        return (
          <FormalGameBoxPitching
            players={awayPlayers}
            pitchings={gameWithInPlay.away.box.pitchings}
            handleUpdate={handlePitchingUpdate}
          />
        );
      case 'awayPAList':
        return (
          <FormalGamePAList
            players={awayPlayers}
            opponentPlayers={homePlayers}
            PAList={R.filter((PA) => PA.side === 'AWAY', gameWithInPlay.PA_list)}
            handleUpdate={handlePAListUpdate}
            handleRowSave={handleRowSave}
          />
        );
      case 'awayEvents':
        return (
          <FormalGameEvents
            players={awayPlayers}
            PAList={R.filter((PA) => PA.side === 'AWAY', gameWithInPlay.PA_list)}
            handleUpdate={handleEventUpdate}
          />
        );
      case 'awayRunners':
        return (
          <FormalGameRunners
            players={awayPlayers}
            opponentPlayers={homePlayers}
            PAList={R.filter((PA) => PA.side === 'AWAY', gameWithInPlay.PA_list)}
            handleUpdate={handleRunnerUpdate}
            handleRowSave={handleRowSave}
          />
        );
      case 'awayChances':
        return <FormalGameChances PAList={R.filter((PA) => PA.side === 'AWAY', gameWithInPlay.PA_list)} />;
      case 'awaySubstitutions':
        return <FormalGameSubstitutions PAList={R.filter((PA) => PA.side === 'AWAY', gameWithInPlay.PA_list)} />;
      case 'homeRecord':
        return <FormalGameRecord players={homeRecordTable} handleUpdate={handleRecordUpdate} />;
      case 'homeBoxBatting':
        return (
          <FormalGameBoxBatting
            players={homePlayers}
            battings={gameWithInPlay.home.box.battings}
            handleUpdate={handleBattingUpdate}
          />
        );
      case 'homeBoxPitching':
        return (
          <FormalGameBoxPitching
            players={homePlayers}
            pitchings={gameWithInPlay.home.box.pitchings}
            handleUpdate={handlePitchingUpdate}
          />
        );
      case 'homePAList':
        return (
          <FormalGamePAList
            players={homePlayers}
            opponentPlayers={awayPlayers}
            PAList={R.filter((PA) => PA.side === 'HOME', gameWithInPlay.PA_list)}
            handleUpdate={handlePAListUpdate}
            handleRowSave={handleRowSave}
          />
        );
      case 'homeEvents':
        return (
          <FormalGameEvents
            players={homePlayers}
            PAList={R.filter((PA) => PA.side === 'HOME', gameWithInPlay.PA_list)}
            handleUpdate={handleEventUpdate}
          />
        );
      case 'homeRunners':
        return (
          <FormalGameRunners
            players={homePlayers}
            opponentPlayers={awayPlayers}
            PAList={R.filter((PA) => PA.side === 'HOME', gameWithInPlay.PA_list)}
            handleUpdate={handleRunnerUpdate}
            handleRowSave={handleRowSave}
          />
        );
      case 'homeChances':
        return <FormalGameChances PAList={R.filter((PA) => PA.side === 'HOME', gameWithInPlay.PA_list)} />;
      case 'homeSubstitutions':
        return <FormalGameSubstitutions PAList={R.filter((PA) => PA.side === 'HOME', gameWithInPlay.PA_list)} />;
      default:
        return <>{part}</>;
    }
  };

  return (
    <CustomDiv>
      <div className="greet">
        {organizer.logo_url ? (
          <img className="greet--pic" src={organizer.logo_url} alt="" />
        ) : (
          <div className="greet--pic default"></div>
        )}
        <div className="greet--name">
          {organizer.name}
          {' - '}
          {tournamentInfo.title}
        </div>
      </div>
      <div className="panel">
        <div className="section-list">
          <div className="section-list--title"></div>
          <Link
            className="section-list--item"
            to={() => ({
              pathname: `/organizer/${organizer.uniqid}/tournament/${tournamentInfo.uniqid}/schedule`,
            })}
          >
            <StyledBackIcon />
            {Multilingual(`GAME.SIDEBAR.BACK`)}
          </Link>
          {Object.keys(partMapping).map(partLink)}
        </div>
        <div className={`section lg`}>
          <div className="section--title">
            {gameTitle}
            <MessageDiv>
              {messages.missingMessages.map((m, i) => (
                <p key={i} className="missing">
                  {m}
                </p>
              ))}
              {messages.errorMessages.map((m, i) => (
                <p key={i} className="error">
                  {m}
                </p>
              ))}
            </MessageDiv>
          </div>
          {inPlayLoading || updating ? <Loading /> : getSection()}
        </div>
      </div>
    </CustomDiv>
  );
};

export default FormalGame;
